import { useEffect, useRef, type RefObject } from "react";
import { EditorState } from "@codemirror/state";
import { EditorView, keymap, lineNumbers, drawSelection } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap, indentWithTab } from "@codemirror/commands";

interface UseCodeMirrorOptions {
  containerRef: RefObject<HTMLDivElement | null>;
  value: string;
  onChange: (value: string) => void;
  onCursorMove?: (lineNum: number) => void;
}

function createEditorState(
  doc: string,
  onChangeRef: RefObject<(v: string) => void>,
  onCursorMoveRef: RefObject<((lineNum: number) => void) | undefined>,
  isProgrammaticRef: RefObject<boolean>,
) {
  const updateListener = EditorView.updateListener.of((update) => {
    if (update.docChanged && !isProgrammaticRef.current) {
      onChangeRef.current(update.state.doc.toString());
    }
    if (update.selectionSet) {
      const mainSel = update.state.selection.main;
      const line = update.state.doc.lineAt(mainSel.head);
      onCursorMoveRef.current?.(line.number - 1);
    }
  });

  return EditorState.create({
    doc,
    extensions: [
      lineNumbers(),
      history(),
      drawSelection(),
      keymap.of([indentWithTab, ...defaultKeymap, ...historyKeymap]),
      updateListener,
      EditorView.lineWrapping,
      EditorView.theme({ ".cm-scroller": { overflow: "auto" }, "&": { height: "100%" } }),
    ],
  });
}

function syncExternalValue(view: EditorView, value: string, isProgrammaticRef: RefObject<boolean>) {
  const currentValue = view.state.doc.toString();
  if (currentValue === value) {
    return;
  }
  const savedScrollTop = view.scrollDOM.scrollTop;
  isProgrammaticRef.current = true;
  view.dispatch({ changes: { from: 0, insert: value, to: view.state.doc.length } });
  isProgrammaticRef.current = false;
  requestAnimationFrame(() => {
    view.scrollDOM.scrollTop = savedScrollTop;
  });
}

export function useCodeMirror({
  containerRef,
  onChange,
  onCursorMove,
  value,
}: UseCodeMirrorOptions): RefObject<EditorView | null> {
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  const onCursorMoveRef = useRef(onCursorMove);
  const isProgrammaticRef = useRef(false);

  onChangeRef.current = onChange;
  onCursorMoveRef.current = onCursorMove;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const state = createEditorState(value, onChangeRef, onCursorMoveRef, isProgrammaticRef);
    const view = new EditorView({ parent: container, state });
    viewRef.current = view;
    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const view = viewRef.current;
    if (view) {
      syncExternalValue(view, value, isProgrammaticRef);
    }
  }, [value]);

  return viewRef;
}
